import { EyeOff } from "@untitledui/icons";
import type { Selection } from "react-aria-components";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { SelectAllPagesButton } from "@/components/application/table/select-all-pages-button";
import { Table } from "@/components/application/table/table";
import { UnboxTableEmptyStateContent } from "@/components/application/table/unbox-table";
import { AdThumbnailBadge } from "@/components/assurance/badges/ad-campaign-badge";
import { Badge } from "@/components/base/badges/badges";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import type { SelectAllState } from "@/hooks/use-select-all-across-pages";
import { cx } from "@/utils/cx";
import "../../../table-styles.css";
import { SEEN_ROW_CLASS, useSeenRows } from "@/components/application/table/use-seen-rows";
import { FLAGGED_VIDEOS_TABLE_KEY } from "@/pages/safety-beta/flagged/components/VideoProgressRow";
import type { VideoItem } from "../../../components/VideoDetail";
import { RiskSeverityDot } from "../../components/RiskSeverityDot";
import { RisksActionsDropdown } from "./RisksActionsDropdown";

type NeedsReviewTableProps = {
  videos: VideoItem[];
  total: number;
  selectedId: string;
  onSelect: (id: string) => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  // True while the detail panel is closed — the table takes the full row width instead
  // of its usual fixed width.
  fullWidth?: boolean;
  selected: string[];
  onSelectionChange: (keys: Selection) => void;
  selectedCount: number;
  selectAll: SelectAllState;
  onConfirm: () => void;
  onConfirmWithoutPolicies: () => void;
  onDismiss: () => void;
  onAddRisk: () => void;
  bulkActionsDisabled?: boolean;
  // videoIds currently animating out after a bulk action — see TabReview's animateOutThenRun.
  exitingIds: Set<string>;
};

export const NeedsReviewTable = ({
  videos,
  total,
  selectedId,
  onSelect,
  hasActiveFilters,
  onClearFilters,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  fullWidth,
  selected,
  onSelectionChange,
  selectedCount,
  selectAll,
  onConfirm,
  onConfirmWithoutPolicies,
  onDismiss,
  onAddRisk,
  bulkActionsDisabled,
  exitingIds,
}: NeedsReviewTableProps) => {
  const { isSeen, markSeen, seenCount } = useSeenRows(FLAGGED_VIDEOS_TABLE_KEY);
  const { scrollRef, onScroll } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage: onLoadMore,
    itemCount: videos.length,
  });

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className={`${fullWidth ? "w-full" : "w-[calc(44%-1rem)]"} shrink-0 border border-border-primary rounded-md overflow-y-auto bg-white h-full`}
    >
      <Table
        size="sm"
        aria-label="Needs Review videos"
        selectionMode="multiple"
        selectedKeys={selected}
        onSelectionChange={onSelectionChange}
        onRowAction={(key) => {
          markSeen(key as string);
          onSelect(key as string);
        }}
        className="table-fixed"
      >
        <Table.Header className={selectedCount > 0 ? "bg-[#fcf6f1]" : "bg-white"} allowSelectAll>
          <Table.Head
            id="video"
            label={selectedCount > 0 ? undefined : `${total} Videos`}
            isRowHeader
            allowsSorting={selectedCount === 0}
            className={cx(
              selectedCount === 0 &&
                "[&_span]:text-quaternary [&_span]:text-xs [&_span]:font-semibold [&_svg]:text-quaternary",
              fullWidth ? "flagged-table-col-video-full" : "flagged-table-col-video-compact",
            )}
          >
            {selectedCount > 0 && (
              <span className="flex items-center gap-2 text-quaternary text-xs font-semibold">
                {selectedCount.toLocaleString()} selected
                <SelectAllPagesButton state={selectAll} className="text-xs font-semibold" />
              </span>
            )}
          </Table.Head>
          <Table.Head
            id="views"
            label={selectedCount > 0 ? undefined : "Views"}
            className={cx(
              "px-[12px] py-[12px] [&_span]:text-quaternary [&_span]:text-xs [&_span]:font-semibold [&_svg]:text-quaternary",
              fullWidth ? "flagged-table-col-views-full" : "flagged-table-col-views-compact",
            )}
          />
          <Table.Head
            id="gmv"
            label={selectedCount > 0 ? undefined : "GMV"}
            allowsSorting={selectedCount === 0}
            className={cx(
              selectedCount === 0
                ? "pl-[6px] [&_span]:text-quaternary [&_span]:text-xs [&_span]:font-semibold [&_svg]:text-quaternary"
                : !fullWidth && "px-0!",
              fullWidth ? "flagged-table-col-gmv-full" : "flagged-table-col-gmv-compact",
            )}
          >
            {selectedCount > 0 && !fullWidth && (
              <div className="flex justify-end w-full">
                <RisksActionsDropdown
                  onConfirm={onConfirm}
                  onConfirmWithoutPolicies={onConfirmWithoutPolicies}
                  onDismiss={onDismiss}
                  onAddRisk={onAddRisk}
                  disabled={bulkActionsDisabled}
                />
              </div>
            )}
          </Table.Head>
          {fullWidth && (
            <Table.Head
              id="risks"
              label={selectedCount > 0 ? undefined : "Risks"}
              className={cx(
                "flagged-table-col-risks-full",
                selectedCount === 0
                  ? "[&_span]:text-quaternary [&_span]:text-xs [&_span]:font-semibold [&_svg]:text-quaternary"
                  : "px-0!",
              )}
            >
              {selectedCount > 0 && (
                <div className="flex justify-end w-full">
                  <RisksActionsDropdown
                    onConfirm={onConfirm}
                    onConfirmWithoutPolicies={onConfirmWithoutPolicies}
                    onDismiss={onDismiss}
                    onAddRisk={onAddRisk}
                    disabled={bulkActionsDisabled}
                  />
                </div>
              )}
            </Table.Head>
          )}
        </Table.Header>
        <Table.Body
          items={videos}
          dependencies={[seenCount]}
          renderEmptyState={() => (
            <div className="h-full flex items-center justify-center">
              <UnboxTableEmptyStateContent
                rows={[]}
                hasActiveFilters={hasActiveFilters}
                filteredEmptyState={{ onClearFilters }}
              />
            </div>
          )}
        >
          {(video) => (
            <Table.Row
              id={video.id}
              className={cx(
                "transition-opacity duration-300",
                video.id === selectedId ? "bg-[#fcf6f1]" : "",
                "cursor-pointer hover:bg-unbox-cream-25",
                exitingIds.has(video.id) && "opacity-0 pointer-events-none",
              )}
            >
              {/* Video: thumbnail + title + creator */}
              <Table.Cell className={cx("pl-[12px] pr-[6px] py-[16px]", isSeen(video.id) && SEEN_ROW_CLASS)}>
                <div className={cx("flex items-center gap-3 p-0", fullWidth ? "justify-start" : "")}>
                  {video.unableToAccess ? (
                    <div className="shrink-0 w-[40px] h-[40px] rounded-sm bg-gray-100 flex items-center justify-center">
                      <EyeOff className="w-4 h-4 text-fg-quaternary" />
                    </div>
                  ) : (
                    <div className="relative shrink-0 w-[40px] h-[40px]">
                      <div
                        className="w-[40px] h-[40px] rounded-sm overflow-hidden"
                        style={
                          !video.urlFirstFrame ? { background: "linear-gradient(135deg, #FFD6A5, #FF8FA3)" } : undefined
                        }
                      >
                        {video.urlFirstFrame && (
                          <img src={video.urlFirstFrame} alt={video.title} className="w-full h-full object-cover" />
                        )}
                      </div>
                      {video.isInAdCampaign && <AdThumbnailBadge />}
                    </div>
                  )}
                  <div className="flex flex-col min-w-0">
                    <Tooltip title={video.title}>
                      <TooltipTrigger className="inline-flex w-full min-w-0 justify-start">
                        <p className="text-xs font-medium text-primary truncate">{video.title}</p>
                      </TooltipTrigger>
                    </Tooltip>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="text-xs text-tertiary truncate">{video.creator}</p>
                      {video.unableToAccess && (
                        // TODO(UNB-1741): match the full "Unable to access" row design from Figma (node 1871-30365)
                        <Badge size="sm" color="gray">
                          Unable to access
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </Table.Cell>
              {/* Views + posted */}
              <Table.Cell
                className={cx(
                  "px-[12px] py-[16px]",
                  fullWidth ? "flagged-table-col-views-full" : "flagged-table-col-views-compact",
                )}
              >
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-secondary">{video.views}</span>
                  <span className="text-xs text-tertiary">Posted {video.postedAt}</span>
                </div>
              </Table.Cell>
              {/* GMV */}
              <Table.Cell
                className={cx(
                  "pl-[6px] pr-[12px] py-[16px] text-xs font-medium text-secondary whitespace-nowrap text-center",
                  fullWidth ? "flagged-table-col-gmv-full text-left" : "flagged-table-col-gmv-compact",
                )}
              >
                {video.gmv}
              </Table.Cell>
              {/* Risks */}
              {fullWidth && (
                <Table.Cell className="px-[6px] py-[16px] flagged-table-col-risks-full">
                  <div className="flex flex-col gap-0.5 min-w-0">
                    {video.topFlags && (
                      <RiskSeverityDot severity={video.topFlags.severity} label={video.topFlags.name} />
                    )}
                    {(video.flagCount ?? 0) > 1 && (
                      <span className="text-xs text-tertiary">+{(video.flagCount ?? 0) - 1} more</span>
                    )}
                  </div>
                </Table.Cell>
              )}
            </Table.Row>
          )}
        </Table.Body>
      </Table>
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-3">
          <LoadingIndicator size="xs" />
        </div>
      )}
    </div>
  );
};
